import { useEffect, useState } from 'react';
import { Alert, AlertColor, Box, Card, CardContent, CircularProgress, FormControl, Grid, InputLabel, ListItemText, MenuItem, OutlinedInput, Paper, Select, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';

import { useVForm } from 'shared/forms';

import { FerramentasDeDetalhe, InputError, ConfirmacaoModal } from 'shared/components';
import { LayoutBaseDePagina } from 'shared/layouts';
import { t } from 'i18next';
import { ICreateUsuarioRole, InicitalCreateUsuarioRole } from 'shared/types';
import { useHasPermissionAcao } from 'shared/hooks';
import { IKeyValue, listagemRoleToIKeyValue } from 'shared/utils';
import { UsuariosService } from 'shared/services/usuarios/usuariosService';
import { RolesService } from 'shared/services/roles/rolesService';


const validationSchema = yup.object({
  IdRole: yup.number().required().moreThan(0),
});

export const DetalheDeUsuarioRole: React.FC = () => {
  const {idUsuario} = useParams<'idUsuario'>();
  const { id = 'novo' } = useParams<'id'>();
  const navigate = useNavigate();

  const hasPermitionCreate = useHasPermissionAcao('usuario','CRE');
  //const hasPermitionDelete = useHasPermissionAcao('usuario','DEL');
  const hasPermitionUpdate = false;

  
  const [alert, setAlert] = useState<null | { text: string; type: AlertColor }>(null);
  const { formRef, saveAndClose, isSaveAndClose } = useVForm();

  const { register, handleSubmit, reset, watch, formState: { errors }, } = useForm({
    resolver: yupResolver(validationSchema),
    reValidateMode: 'onBlur'
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingRole, setIsLoadingRole] = useState(false);
  const [openConfirmacao, setOpenConfirmacao] = useState(false);
  const [titleConfirmacao, setTitleConfirmacao] = useState('');
  const [messageConfirmacao, setMessageConfirmacao] = useState('');

  const selectRole = watch('IdRole');

  const [listKeyValueRole, setListKeyValueRole] = useState<IKeyValue[]>([]);

  
  useEffect(() => {
    setIsLoadingRole(true);

    RolesService.getAll()
      .then((result) => {
        setIsLoadingRole(false);
        if (result instanceof Error) {
          console.log(`Ocorreu o seguinte erro: ${result.message}`);
        } else {
          setListKeyValueRole(listagemRoleToIKeyValue(result));
          console.log('listagemRoleToIKeyValue(result)',listagemRoleToIKeyValue(result));
        }
      });
  },[]);

  useEffect(() => {
    setAlert(null);
    if (id !== 'novo') {
      setIsLoading(true);

      UsuariosService.getUsuarioRole(Number(idUsuario), Number(id))
        .then((result) => {
          if (result instanceof Error) {
            setAlert({
              text: result.message,
              type: 'error',
            });
            navigate(`/usuario/detalhe/${idUsuario}`);
          } else {
            delete result.ROW_NUM;
            reset(result);
          }
          setIsLoading(false);
        });
    }
    else {
      reset(InicitalCreateUsuarioRole);
    }

  }, [id]);

  const handleDelete = () => {
    setTitleConfirmacao(t('components.actions.apagar'));
    setMessageConfirmacao(t('messages.usuarioRole.exclusion') + id + '?');
    setOpenConfirmacao(true);
    //console.log('handleDelete');
  };

  const confirmDelete = () => {
    setAlert(null);
    UsuariosService.deleteUsuarioRole(Number(idUsuario), Number(id))
      .then(result => {
        if (result instanceof Error) {
          setAlert({
            text: result.message,
            type: 'error',
          });
          setOpenConfirmacao(false);
        } else {
          setAlert({
            text: 'Registro apagado com sucesso!',
            type: 'success',
          });
          navigate(`/usuario/detalhe/${idUsuario}`);
        }
      })
      .catch( reason => {
        setAlert({
          text: reason,
          type: 'error',
        });
        setOpenConfirmacao(false);
      });
  };

  const onSubmit = async (data: ICreateUsuarioRole) => {
    setAlert(null);
    setIsSubmitting(true);
    if (id === 'novo') {
      UsuariosService
        .createUsuarioRole(Number(idUsuario), data)
        .then((result) => { 
          setIsLoading(false);
          if (result instanceof Error) {  
            setAlert({
              text: result.message,
              type: 'error',
            });
          } else {
            console.log('isSaveAndClose',isSaveAndClose());
            if (isSaveAndClose()) {
              navigate(`/usuario/detalhe/${idUsuario}/role`);
            } else {
              navigate(`/usuario/detalhe/${idUsuario}/role/${data.IdRole}`);
            }
          }
        })
        .finally(() => {
          setIsSubmitting(false);
        });

    } 
  };



  return (
    <LayoutBaseDePagina
      titulo={id === 'novo' ? t('pages.usuarioRole.newUsuRole') : hasPermitionUpdate?'Alterar Usuário x Role':'Consultar Usuário x Role'}
      barraDeFerramentas={!(isLoading || isSubmitting || isLoadingRole) && (
        <FerramentasDeDetalhe
          idForm='formDetalheDeUsuarioRole'
          mostrarBotaoSalvarEFechar={hasPermitionCreate && id == 'novo'}
          mostrarBotaoSalvar={false}
          mostrarBotaoNovo={false}
          mostrarBotaoApagar={false}
          aoClicarEmVoltar={() => navigate(`/usuario/detalhe/${idUsuario}/role`)}
          aoClicarEmSalvarEFechar={saveAndClose}
          
        />)
      }
    >
      <Box ref={formRef} id='formDetalheDeUsuarioRole' component="form" display="flex" autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
        <Box margin={1} display="flex" flexDirection="column" component={Paper} variant="outlined" width='100%' >

          <Card >
            <CardContent sx={{ padding: 0 }} >
              <div>
                {alert ? (
                  <>
                    <Grid item xs={12} paddingX={2}>
                      <Alert severity={alert.type} onClose={() => setAlert(null)} sx={{ marginTop: '1rem', marginBottom: '0' }}>
                        {alert.text}
                      </Alert>
                    </Grid>
                  </>
                ) : (
                  <Grid item xs={12} component={Typography} paddingX={2}>

                  </Grid>
                )}
                <Grid container  >
                  <Grid item xs={4} paddingX={2}>
                    <FormControl fullWidth sx={{ marginTop: '1rem', marginBottom: '0' }}>
                      <InputLabel id='label-select-role'>{`${t('pages.usuario.role')} *`}</InputLabel>
                      <Select
                        disabled={isSubmitting || isLoading || isLoadingRole || id !== 'novo'}
                        error={errors?.IdRole?.type ? true : false}
                        labelId='label-select-role'
                        id='IdRole'
                        input={<OutlinedInput label={`${t('pages.usuario.role')} *`} />}
                        {...register('IdRole')}
                        value={selectRole || ''}
                      >
                        <MenuItem value=''>
                          <ListItemText primary='' />
                        </MenuItem>
                        {listKeyValueRole.map((item) => (
                          <MenuItem key={item.key} value={item.key} >
                            <ListItemText primary={item.value} sx={{ margin: '0' }} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {errors?.IdRole?.type && <InputError type={errors.IdRole.type} field='IdRole' />}
                  </Grid>
                </Grid>
              </div>
            </CardContent>
          </Card>
        </Box>
        <ConfirmacaoModal
          open={openConfirmacao}
          title={titleConfirmacao}
          message={messageConfirmacao}
          onClose={() => setOpenConfirmacao(false)}
          onConfirm={() => confirmDelete()}
        />
      </Box>
      {(isLoading || isSubmitting || isLoadingRole) && (<Box sx={{
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
        position: 'absolute',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <CircularProgress />
      </Box>)}

    </LayoutBaseDePagina >
  );
};
